/**
 * An ISO-8601 duration with day and time parts only, which is the shape `java.time.Duration` writes.
 * Years, months and weeks are not matched, because their length depends on the calendar.
 */
const ISO_DURATION = /^P(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i;

/** The units a duration is written in, largest first. */
const UNITS: { label: string; seconds: number }[] = [
    { label: 'd', seconds: 86400 },
    { label: 'h', seconds: 3600 },
    { label: 'min', seconds: 60 },
    { label: 's', seconds: 1 },
];

/**
 * The length of an ISO-8601 duration in seconds, or `undefined` when the value is not one.
 * `PT1H30M` is 5400, and `P1DT2H` is 93600. Accepts `unknown` because callers pass untyped JSON.
 */
export function parseIsoDuration(text: unknown): number | undefined {
    const match = ISO_DURATION.exec(typeof text === 'string' ? text.trim() : '');
    /* A bare `P` or `PT` matches the pattern but names no length. */
    if (!match || match.slice(1).every((part) => part === undefined)) {
        return undefined;
    }
    const [days, hours, minutes, seconds] = match.slice(1).map((part) => Number(part ?? 0));
    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
}

/**
 * An ISO-8601 duration in words, largest unit first, or `undefined` when the value is not one.
 * `PT1H30M` reads as `1 h 30 min`, and `PT48H` as `2 d`. Fractions of a second are rounded.
 */
export function formatIsoDuration(text: unknown): string | undefined {
    const total = parseIsoDuration(text);
    if (total === undefined) {
        return undefined;
    }
    let remaining = Math.round(total);
    if (remaining === 0) {
        return '0 s';
    }
    const parts: string[] = [];
    for (const unit of UNITS) {
        const count = Math.floor(remaining / unit.seconds);
        if (count > 0) {
            parts.push(`${count} ${unit.label}`);
            remaining -= count * unit.seconds;
        }
    }
    return parts.join(' ');
}

/** A unit a person types a duration in. */
export type DurationUnit = 'min' | 'h' | 'd';

/** A duration as a number and the unit it is typed in. */
export interface DurationParts {
    value: number;
    unit: DurationUnit;
}

const UNIT_SECONDS: Record<DurationUnit, number> = { d: 86400, h: 3600, min: 60 };

/**
 * A typed duration as ISO-8601 in the spelling `java.time.Duration.toString()` writes, or `undefined` when it is
 * not a positive number. `1 d` is `PT24H`, so a value read back from the service compares equal to one typed in.
 */
export function toIsoDuration(value: unknown, unit: DurationUnit): string | undefined {
    const count = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    if (typeof count !== 'number' || !Number.isFinite(count) || count <= 0) {
        return undefined;
    }
    const total = Math.round(count * UNIT_SECONDS[unit]);
    if (total <= 0) {
        return undefined;
    }
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return 'PT' + (hours ? `${hours}H` : '') + (minutes ? `${minutes}M` : '') + (seconds ? `${seconds}S` : '');
}

/**
 * An ISO-8601 duration as a number in the largest unit that holds it whole, or `undefined` when the value is not
 * one. `PT48H` is `2 d` and `PT90M` is `90 min`; a length that no unit holds whole is given in minutes.
 */
export function splitIsoDuration(text: unknown): DurationParts | undefined {
    const total = parseIsoDuration(text);
    if (total === undefined || total <= 0) {
        return undefined;
    }
    const unit = (['d', 'h'] as DurationUnit[]).find((candidate) => total % UNIT_SECONDS[candidate] === 0) ?? 'min';
    return { value: total / UNIT_SECONDS[unit], unit };
}
